import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { notifications } from "@mantine/notifications";
import type {
  CorridorNotificationTab,
  CorridorUnreadCounts,
  UserNotificationDto,
  UserNotificationType,
} from "@shared/notifications";
import { USER_NOTIFY_SOCKET_EVENT } from "@shared/notifications";
import { useUserContext } from "@/context/UserContext";
import trpc, { getToken } from "@/shared/utils/trpc";
import { getPortalSocketPath, getPortalSocketUrl } from "@/shared/constants/portalSocket.const";
import { getActiveCollaborationChatMatchId } from "@/shared/utils/activeChatPresence";
import { NOTIFICATION_AUTO_CLOSE_MS } from "@/shared/constants/notification.const";
import { notifyMyClusterCountChanged } from "@/pages/my-cluster/myCluster.const";
import { notifyExhibitorInquiryCountChanged } from "@/pages/my-expos/exhibitorInquiries.const";

type NotificationContextValue = {
  items: UserNotificationDto[];
  unreadCount: number;
  /** Unread company-side updates per membership link and corridor tab. */
  corridorUnread: CorridorUnreadCounts;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  markCorridorTabRead: (linkId: string, tab: CorridorNotificationTab) => Promise<void>;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const EMPTY_CORRIDOR_UNREAD: CorridorUnreadCounts = { byLink: {}, total: 0 };

/** Fired on `window` for every live notification so layouts can refresh their own badge counts. */
export const USER_NOTIFICATION_RECEIVED_EVENT = "gt:user-notification-received";

const PENDING_COUNT_NOTIFICATION_TYPES = new Set<UserNotificationType>([
  "REQUEST_RECEIVED",
  "REQUEST_ACCEPTED",
  "REQUEST_DECLINED",
]);

const MY_CLUSTER_COUNT_NOTIFICATION_TYPES = new Set<UserNotificationType>([
  "ECOSYSTEM_HEAD_JOIN_ACCEPTED",
  "ECOSYSTEM_HEAD_JOIN_REJECTED",
  "ECOSYSTEM_HEAD_COMPANY_REMOVED",
]);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const {
    isLoggedIn,
    userDetails,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  } = useUserContext();
  const [items, setItems] = useState<UserNotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [corridorUnread, setCorridorUnread] = useState<CorridorUnreadCounts>(EMPTY_CORRIDOR_UNREAD);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const refreshPendingInterestCountRef = useRef(refreshPendingInterestCount);
  const refreshCollaborationSidebarCountsRef = useRef(refreshCollaborationSidebarCounts);
  refreshPendingInterestCountRef.current = refreshPendingInterestCount;
  refreshCollaborationSidebarCountsRef.current = refreshCollaborationSidebarCounts;

  const refreshCorridorUnread = useCallback(async () => {
    try {
      setCorridorUnread(await trpc.notification.corridorUnreadCounts.query());
    } catch (err) {
      console.error("[notifications] corridor unread refresh failed:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      setCorridorUnread(EMPTY_CORRIDOR_UNREAD);
      return;
    }
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        trpc.notification.list.query({ page: 1, limit: 30 }),
        trpc.notification.unreadCount.query(),
        refreshCorridorUnread(),
      ]);
      setItems(listRes.items as UserNotificationDto[]);
      setUnreadCount(countRes.count);
    } catch (err) {
      console.error("[notifications] refresh failed:", err);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, refreshCorridorUnread]);

  const markRead = useCallback(
    async (id: string) => {
      try {
        await trpc.notification.markRead.mutate({ id });
        const now = new Date().toISOString();
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: now } : n)));
        setUnreadCount((c) => Math.max(0, c - 1));
        void refreshCorridorUnread();
      } catch (err) {
        console.error("[notifications] markRead failed:", err);
      }
    },
    [refreshCorridorUnread],
  );

  const markAllRead = useCallback(async () => {
    try {
      await trpc.notification.markAllRead.mutate();
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? now })));
      setUnreadCount(0);
      setCorridorUnread(EMPTY_CORRIDOR_UNREAD);
    } catch (err) {
      console.error("[notifications] markAllRead failed:", err);
    }
  }, []);

  const markCorridorTabRead = useCallback(async (linkId: string, tab: CorridorNotificationTab) => {
    try {
      const { count } = await trpc.notification.markReadForCorridorTab.mutate({ linkId, tab });
      if (count <= 0) return;
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((n) =>
          !n.readAt && n.metadata?.linkId === linkId && n.metadata?.corridorTab === tab ? { ...n, readAt: now } : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - count));
      setCorridorUnread((prev) => {
        const linkCounts = prev.byLink[linkId];
        const cleared = linkCounts?.[tab] ?? 0;
        if (!linkCounts || cleared === 0) return prev;
        const { [tab]: _removed, ...rest } = linkCounts;
        return { byLink: { ...prev.byLink, [linkId]: rest }, total: Math.max(0, prev.total - cleared) };
      });
    } catch (err) {
      console.error("[notifications] markCorridorTabRead failed:", err);
    }
  }, []);

  const handleIncoming = useCallback((dto: UserNotificationDto) => {
    const activeChatMatchId = getActiveCollaborationChatMatchId();
    const chatMatchId =
      dto.type === "CHAT_MESSAGE" && dto.metadata && typeof dto.metadata.matchId === "string"
        ? dto.metadata.matchId
        : null;
    if (dto.type === "CHAT_MESSAGE" && chatMatchId && chatMatchId === activeChatMatchId) {
      return;
    }

    if (!dto.readAt) {
      setUnreadCount((c) => c + 1);
    }
    setItems((prev) => {
      if (prev.some((n) => n.id === dto.id)) return prev;
      return [dto, ...prev].slice(0, 50);
    });
    if (document.visibilityState === "visible") {
      notifications.show({
        title: dto.title,
        message: dto.body,
        color: "blue",
        autoClose: NOTIFICATION_AUTO_CLOSE_MS,
      });
    }

    if (PENDING_COUNT_NOTIFICATION_TYPES.has(dto.type)) {
      refreshPendingInterestCountRef.current();
      refreshCollaborationSidebarCountsRef.current();
    }
    if (MY_CLUSTER_COUNT_NOTIFICATION_TYPES.has(dto.type)) {
      notifyMyClusterCountChanged();
    }
    if (dto.type === "EXPO_COMPANY_REACHOUT") {
      notifyExhibitorInquiryCountChanged();
    }
    if (dto.metadata?.corridorTab) {
      void refreshCorridorUnread();
    }
    window.dispatchEvent(new CustomEvent<UserNotificationDto>(USER_NOTIFICATION_RECEIVED_EVENT, { detail: dto }));
  }, [refreshCorridorUnread]);

  useEffect(() => {
    if (!isLoggedIn || !userDetails?.id) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setItems([]);
      setUnreadCount(0);
      setCorridorUnread(EMPTY_CORRIDOR_UNREAD);
      return;
    }

    void refresh();

    const token = getToken();
    if (!token) return;

    const socketUrl = getPortalSocketUrl();
    if (!socketUrl) return;

    const socket = io(socketUrl, {
      path: getPortalSocketPath(),
      auth: { token },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on(USER_NOTIFY_SOCKET_EVENT, (payload: UserNotificationDto) => {
      if (payload?.id) handleIncoming(payload);
    });

    return () => {
      socket.off(USER_NOTIFY_SOCKET_EVENT);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [isLoggedIn, userDetails?.id, refresh, handleIncoming]);

  const value: NotificationContextValue = {
    items,
    unreadCount,
    corridorUnread,
    loading,
    refresh,
    markRead,
    markAllRead,
    markCorridorTabRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
